import React, { Fragment } from 'react';
import { bindActionCreators } from 'redux';
import { useNavigate } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';

// Components
import Button from '@common-components/button/Button';
import Tooltip from '@common-components/tooltip/Tooltip';

// Selectors

import { selectIfJumpingRestricted } from '@reducers/appReducer';

// ActionCreators

import {
    changeData,
    changePage,
    checkBloodRelationships,
    scrollToElement,
    setException,
    submitForm,
    updateDisabledUi
} from '@actions/ssap/ssapActionCreators';

import { showModal, hideAllModals, hideModal } from '@actions/common/actionCreators';

//Utils

import { _destructureProxy } from '@utils/miscellaneous/proxies';
import { getCurrentAndMaxAchievedPages } from '@utils/miscellaneous/getCurrentAndMaxAchievedPages';
import { getCurrentHouseholdMember } from '@utils/householdMember/getCurrentHouseholdMember';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { preventerTools } from '@utils/tools/preventerTools';
import { isFormInInitialState } from '@reducers/appReducer';
import { uniqueId } from '@utils/miscellaneous/objectHelpers';

// Constants

import { configs } from '@constants/configs/configs';
import { EXCEPTIONS } from '@constants/ssap/exceptions/ssapExceptions';
import { ssapModalTypes as MODAL_TYPES } from '@constants/ssap/modals/ssapModalTypes';
import { getButtons } from '@constants/ssap/buttons/buttonsConfiguration';
import { PAGES } from '@constants/ssap/flows/pagesTypes';
import { getDateDifference } from '@utils/dates/date.utils';
import classnames from 'classnames';

/**
 *
 * @param {Object}props
 * @returns {React.Component} - Button with assigned to it actions on click depending on situation
 *
 */
const ButtonContainer  = props => {

    let {
        // These come from directly from Page or other Component
        args,
        customButtonClassName,
        isDisabled = false,
        type,
        withTooltip = false,
        tooltipContent,
        tooltipContentReplacement,
        triggerDecorationClassName,
        // These come from Redux store
        adjustedFlow,
        data,
        data: {
            applyingForFinancialAssistanceIndicator,
            authorizedRepresentativeIndicator,
            isRidpVerified,
            applicationGuid: caseNumber,
            getHelpIndicator,
            maxAchievedPage
        },
        environment: {
            localDates: {
                serverDate
            },
            isState
        },
        exception,
        exception: {
            exceptionType,
            exceptionPages: [ destinationPage, departurePage ]
        },
        form,
        formName,
        history,
        householdMembers,
        isSsapFormsInInitialsState,
        methods,
        page,
        ariaLabel
    } = props;

    const navigate = useNavigate();
    const isJumpingRestricted = useSelector(selectIfJumpingRestricted);

    const {
        isEligiblyForSave,
    } = preventerTools.provideObject(form[formName]);

    const {
        hhmIndex: currentHhmIndex,
        currentHhm,
    } = getCurrentHouseholdMember(adjustedFlow, page, householdMembers);

    const _pp = (pageName, hhmIndex = undefined) => {
        return adjustedFlow.findIndex(page => {
            return hhmIndex !== undefined ?
                page.pageName === pageName && page.hhm === hhmIndex :
                page.pageName === pageName;
        });
    };

    const captureRadioButtonOnSaveGTM = () => {
        const getYesOrNo = value => {
            let val = value;
            if (typeof value === 'string' || value instanceof String) {
                val = value.toLowerCase();
            }
            if (val && val !== 'false') {
                return 'Yes';
            } else if (val === 'false' || val === false) {
                return 'No';
            } else {
                return value;
            }
        };

        const {
            ethnicityAndRace,
            healthCoverage
        } = preventerTools.provideObject(currentHhm, {
            ethnicityAndRace: {
                ethnicity: [],
                race: []
            },
            healthCoverage: {}
        });

        const triggerEvent = EVENTS => {  
            switch(formName) {
                case 'assistanceForm':
                    window.dataLayer.push(EVENTS.HELP);
                    break;
                case 'financialAssistanceForm':
                    window.dataLayer.push(EVENTS.COVERAGE);
                    window.dataLayer.push(EVENTS.REP);
                    break;
                case 'additionalInfoForm':
                    window.dataLayer.push(EVENTS.ADDITIONAL);
                    break;
                case 'ethnicityRaceForm':
                    window.dataLayer.push(EVENTS.ORIGIN);
                    break;
                default:
            }
        };

        const EVENTS = {
            ADDITIONAL: { event: `help_paying_for_medical_bills_from_the_last_3_months?::${getYesOrNo(healthCoverage.unpaidBill)}` },
            COVERAGE: { event: `help_paying_for_health_coverage?::${getYesOrNo(applyingForFinancialAssistanceIndicator)}` },
            HELP: { event: `who_is_helping_you?::${getHelpIndicator}` },
            ORIGIN: { event: `Hispanic_Latino_or_Spanish_origin?::${getYesOrNo(ethnicityAndRace && ethnicityAndRace.hispanicLatinoSpanishOriginIndicator)}` },
            REP: { event: `Authorized_Representative?::${getYesOrNo(authorizedRepresentativeIndicator)}` }
        };

        if(i18n === 'button.save_exit' ||  i18n === 'button.save_continue') { 
            triggerEvent(EVENTS);
        }
    };

    const getActions = () => {

        const {
            changeData,
            changePage,
            scrollToElement,
            setException,
            showModal
        } = props.actions;

        const exc = (exception => exception ? exception : null)(exceptionType);

        const handleRIDP = () => {
            return () => {
                if(isRidpVerified) {
                    changePage(_pp(PAGES.SIGN_AND_SUBMIT_PAGE));
                    changeData({
                        ...data,
                        ...getCurrentAndMaxAchievedPages(
                            'increase',
                            page,
                            maxAchievedPage,
                            false
                        )
                    });
                } else {
                    navigate(`${configs.routes.RIDP}?caseNumber=${caseNumber}`);
                }

            };
            
        };

        const handleHouseholdCheck = (...callbacks) => {

            return () => {

                const {
                    agedMembersList,
                    nonApplyingForCoverageMembersList
                } = householdMembers.reduce(
                    (memberLists, hhm) => {

                        const {
                            applyingForCoverageIndicator,
                            dateOfBirth,
                            name
                        } = hhm;

                        const age = getDateDifference(serverDate, dateOfBirth, 'year');


                        if(!applyingForCoverageIndicator) {
                            memberLists = {
                                ...memberLists,
                                nonApplyingForCoverageMembersList: [
                                    ...memberLists.nonApplyingForCoverageMembersList,
                                    normalizeNameOfPerson(name)
                                ]
                            };
                        }

                        if(age >= 65 && applyingForCoverageIndicator) {
                            memberLists = {
                                ...memberLists,
                                agedMembersList: [
                                    ...memberLists.agedMembersList,
                                    normalizeNameOfPerson(name)
                                ]
                            };
                        }

                        return memberLists;
                    },
                    {
                        agedMembersList: [],
                        nonApplyingForCoverageMembersList: []
                    }
                );

                switch(true) {
                    case nonApplyingForCoverageMembersList.length === householdMembers.length:
                        showModal({
                            modalType: MODAL_TYPES.MANDATORY_SEEKING_FOR_COVERAGE_MODAL
                        });
                        break;

                    case agedMembersList.length > 0 && isEligiblyForSave:
                        showModal({
                            modalType: MODAL_TYPES.INFORM_MEDICARE_ELIGIBLE_MODAL,
                            modalData: {
                                callbacks: [
                                    () => methods[formName].onFormSubmit('increase', callbacks)
                                ],
                                membersList: agedMembersList,
                            }
                        });
                        break;

                    default:
                        methods[formName].onFormSubmit('increase', callbacks);
                }
            };
        };

        const handleMultiTaxHouseholds = (...callbacks) => {
            return () => {
                methods.Page2_07_E2_mod1.handleSaveClick.apply(this, [
                    () => methods[formName].onFormSubmit('increase', callbacks),
                    householdMembers
                ]);
            };
        };

        const handleBloodRelationshipsCheck = (...callbacks) => {
            return () => {
                methods[formName].onFormSubmitForValidationOnly(
                    [
                        () => props.actions.checkBloodRelationships(
                            () => methods[formName].onFormSubmit('increase', callbacks)
                        ),
                        props.actions.hideAllModals
                    ],
                    props.actions.hideAllModals
                );
            };
        };

        const lawfullyPresentCheck = (...callbacks) => {

            return () => {
                const {
                    citizenshipStatusIndicator,
                    eligibleImmigrationDocumentType
                } = currentHhm.citizenshipImmigrationStatus;
                if(!citizenshipStatusIndicator && !Object.values(eligibleImmigrationDocumentType[0]).some(doc => doc)){
                    showModal({
                        modalType: MODAL_TYPES.INFORM_LAWFULLY_PRESENCE_REQUIRED_FOR_COVERAGE_MODAL,
                        modalData: {
                            callbacks: [
                                () => methods[formName].onFormSubmit('increase', callbacks)
                            ]
                        }
                    });
                } else {
                    methods[formName].onFormSubmit('increase', callbacks);
                }
            };
        };

        const expectedIncomeCheck = (...callbacks) => {
            return () => {
                const {
                    annualTaxIncome:{
                        reportedIncome,
                        projectedIncome,
                    }
                } = currentHhm;

                if(!reportedIncome && projectedIncome && isState.NJ){
                    showModal({
                        modalType: MODAL_TYPES.EXPECTED_INCOME_MODAL,
                        modalData: {
                            callbacks: [
                                () => methods[formName].onFormSubmit('increase', callbacks)
                            ],
                            reportedIncome,
                            onGoBack: () => methods[formName].onFormExit([() => changePage(_pp(PAGES.INCOME_SOURCES_PAGE, currentHhmIndex))])
                        }
                    });
                } else {
                    methods[formName].onFormSubmit('increase', callbacks, null, data);
                }
            };
        };

        const handleFormCheck = action => {
            if(!isSsapFormsInInitialsState) {
                props.actions.showModal({
                    modalType: MODAL_TYPES.LEAVE_PAGE_MODAL,
                    modalData: {
                        action,
                    }
                });
            } else {
                return action();
            }
        };

        // Here we assign needed ActionCreators depending on (checking in order):

        // 1. Type of configuring button
        // 2. Page for which we take button
        // 3. Other condition that should be true

        // Sometimes we do not use ActionCreators, but method from Component via passing needed context

        switch(type) {

            case 'leftButton':
                return () => {
                    if(exc && page === destinationPage) {
                        switch(true) {
                            case exc === EXCEPTIONS.EDITING_PRIMARY_HOUSEHOLD_CONTACT:
                            case exc === EXCEPTIONS.EDITING_HOUSEHOLD_MEMBER:
                            case exc === EXCEPTIONS.EDITING_HOUSEHOLD_MEMBER_ADDRESS:
                                handleFormCheck(() => methods[formName].onFormExit([() => changePage(_pp(PAGES.HOUSEHOLD_SUMMARY_PAGE))]));
                                break;

                            case exc === EXCEPTIONS.ADDING_HOUSEHOLD_MEMBER_FROM_HOUSEHOLD_SECTION:
                                handleFormCheck(() => methods[formName].onFormExit([() => changePage(_pp(PAGES.TAX_HOUSEHOLD_COMPOSITION_PAGE))]));
                                break;

                            case exc === EXCEPTIONS.REVIEW_HOUSEHOLD_SECTION:
                                handleFormCheck(() => methods[formName].onFormExit([() => changePage(_pp(PAGES.FAMILY_AND_HOUSEHOLD_SUMMARY_PAGE))]));
                                break;

                            case exc === EXCEPTIONS.EDITING_INCOMES_AND_DEDUCTIONS:
                                handleFormCheck(() => methods[formName].onFormExit([() => changePage(_pp(PAGES.PERSONAL_INCOME_SUMMARY_PAGE, currentHhmIndex))], data));
                                break;

                            case exc === EXCEPTIONS.REVIEW_INCOME_SECTION:
                                handleFormCheck(() => methods[formName].onFormExit([() => changePage(_pp(PAGES.INCOME_INFORMATION_SUMMARY_PAGE))], data));
                                break;

                            case exc === EXCEPTIONS.EDITING_ADDITIONAL_INFORMATION:
                                handleFormCheck(() => methods[formName].onFormExit([() => changePage(_pp(PAGES.ADDITIONAL_INFORMATION_SUMMARY_PAGE))]));
                                break;

                            case exc === EXCEPTIONS.FIXING_TAX_RELATIONSHIP:
                                handleFormCheck(() => methods[formName].onFormExit([() => changePage(_pp(PAGES.TAX_HOUSEHOLD_COMPOSITION_PAGE))]));
                                break;

                            case exc === EXCEPTIONS.REVIEW_BEFORE_SUBMIT_FAMILY_AND_HOUSEHOLD:
                                handleFormCheck(() => methods[formName].onFormExit([() => changePage(_pp(PAGES.FINAL_APPLICATION_REVIEW_PAGE))]));
                                break;

                            case exc === EXCEPTIONS.REVIEW_BEFORE_SUBMIT:
                                handleFormCheck(() => methods[formName].onFormExit([() => changePage(_pp(PAGES.FINAL_APPLICATION_REVIEW_PAGE))], data));
                                break;

                            case exc === EXCEPTIONS.MANAGE_SPOUSE:
                                handleFormCheck(() => methods[formName].onFormExit([() => changePage(departurePage)]));
                        }
                    } else {
                        if(methods[formName]) {
                            switch(page) {
                                case _pp(PAGES.INCOME_SOURCES_PAGE, currentHhmIndex):
                                case _pp(PAGES.DEDUCTION_SOURCES_PAGE, currentHhmIndex):
                                    handleFormCheck(() => methods[formName].onFormExit([() => changePage('decrease')], data));
                                    break;
                                default:
                                    handleFormCheck(() => methods[formName].onFormExit([() => changePage('decrease')]));
                            }
                        } else {
                            changePage('decrease');
                        }
                    }
                };

            case 'middleButton': {
                const callback = (...callbacks) => {
                    switch(page) {
                        case _pp(PAGES.ABOUT_YOUR_HOUSEHOLD_PAGE):
                            (f => f())(handleHouseholdCheck.apply(this, callbacks)); break;
                            
                        case _pp(PAGES.RELATIONSHIPS_PAGE):
                            (f => f())(handleBloodRelationshipsCheck.apply(this, callbacks)); break;

                        case _pp(PAGES.CITIZENSHIP_IMMIGRATION_STATUS_PAGE, currentHhmIndex):
                            (f => f())(lawfullyPresentCheck.apply(this, callbacks)); break;

                        case _pp(PAGES.EXPECTED_INCOME_PAGE, currentHhmIndex):
                            (f => f())(expectedIncomeCheck.apply(this, callbacks)); break;

                        case _pp(PAGES.TAX_HOUSEHOLD_COMPOSITION_PAGE):
                            (f => f())(handleMultiTaxHouseholds.apply(this, callbacks)); break;

                        case _pp(PAGES.INCOME_SOURCES_PAGE, currentHhmIndex):
                        case _pp(PAGES.DEDUCTION_SOURCES_PAGE, currentHhmIndex):
                            methods[formName].onFormSubmit('increase', callbacks, null, data); break;
            
                        default:
                            switch(true) {
                                case exc === EXCEPTIONS.EDITING_PRIMARY_HOUSEHOLD_CONTACT:
                                    methods[formName].onFormSubmit('increase', callbacks);
                                    break;

                                case exc === EXCEPTIONS.REVIEW_HOUSEHOLD_SECTION && _pp(PAGES.ETHNICITY_AND_RACE_PAGE) === page:
                                    methods[formName].onFormSubmit(_pp(PAGES.AMERICAN_INDIAN_ALASKA_NATIVE_PAGE), callbacks);
                                    break;

                                default:
                                    if(methods[formName]) {
                                        methods[formName].onFormSubmit('increase', callbacks);
                                    } else {
                                        changePage('increase');
                                    }
                                    captureRadioButtonOnSaveGTM();
                            }
                    }
                };

                return () => {
                    showModal({
                        modalType: MODAL_TYPES.SSAP_EXIT_MODAL,
                        modalData: {
                            callback,
                            history
                        }
                    });
                };
            }

            case 'rightButton':
                captureRadioButtonOnSaveGTM();
                switch(page) {
                    case _pp(PAGES.FINAL_APPLICATION_REVIEW_PAGE):
                        return handleRIDP();

                    case _pp(PAGES.SIGN_AND_SUBMIT_PAGE):
                        return () => methods.Page3_17.handleSubmit.apply(this, args);

                    case _pp(PAGES.ABOUT_YOUR_HOUSEHOLD_PAGE):
                        return handleHouseholdCheck();

                    case _pp(PAGES.CITIZENSHIP_IMMIGRATION_STATUS_PAGE, currentHhmIndex):
                        return lawfullyPresentCheck();

                    case _pp(PAGES.RELATIONSHIPS_PAGE):
                        return handleBloodRelationshipsCheck();

                    case _pp(PAGES.TAX_HOUSEHOLD_COMPOSITION_PAGE):
                        return handleMultiTaxHouseholds();

                    case _pp(PAGES.INCOME_SOURCES_PAGE, currentHhmIndex):
                    case _pp(PAGES.DEDUCTION_SOURCES_PAGE, currentHhmIndex):
                        return () => methods[formName].onFormSubmit('increase', [], null, data);

                    case _pp(PAGES.EXPECTED_INCOME_PAGE, currentHhmIndex):
                        return expectedIncomeCheck();

                    default:
                        return () => {
                            switch(true) {
                                case exc === EXCEPTIONS.EDITING_PRIMARY_HOUSEHOLD_CONTACT:
                                    methods[formName].onFormSubmit('increase');
                                    break;
                                case exc === EXCEPTIONS.REVIEW_HOUSEHOLD_SECTION && _pp(PAGES.ETHNICITY_AND_RACE_PAGE) === page:
                                    methods[formName].onFormSubmit(_pp(PAGES.AMERICAN_INDIAN_ALASKA_NATIVE_PAGE));
                                    break;
                                default:
                                    if(methods[formName]) {
                                        methods[formName].onFormSubmit('increase');
                                    } else {
                                        const updatedData = {
                                            ...data,
                                            ...getCurrentAndMaxAchievedPages(
                                                'increase',
                                                page,
                                                maxAchievedPage,
                                                false
                                            )
                                        };
                                        changeData(updatedData);
                                        changePage('increase');
                                    }
                            }
                        };
                }

            case 'editButton_secondary':
            case 'editButton_secondary_with_icon':

                switch(page) {

                    case _pp(PAGES.HOUSEHOLD_SUMMARY_PAGE):
                        return () => {
                            const [ type, hhmIndex, payload ] = [...args];

                            switch(type){
                                case 'editMember':
                                    scrollToElement.apply(this, [payload]);

                                    setException(
                                        EXCEPTIONS.EDITING_HOUSEHOLD_MEMBER,
                                        [ _pp(PAGES.ABOUT_YOUR_HOUSEHOLD_PAGE) ],
                                        true
                                    );
                                    break;
                                case 'undoMemberRemoval':
                                    showModal({
                                        modalType: MODAL_TYPES.UNDO_MEMBER_REMOVAL_MODAL,
                                        modalData: {
                                            history
                                        }
                                    });
                                    break;
                                case 'editAddress':
                                case 'editPcp':

                                    setException(
                                        hhmIndex === 0 ?
                                            EXCEPTIONS.EDITING_PRIMARY_HOUSEHOLD_CONTACT :
                                            EXCEPTIONS.EDITING_HOUSEHOLD_MEMBER_ADDRESS,
                                        [
                                            hhmIndex === 0 ?
                                                _pp(PAGES.PRIMARY_CONTACT_INFORMATION_PAGE) :
                                                _pp(PAGES.OTHER_ADDRESSES_PAGE)
                                        ],
                                        true
                                    );
                            }
                        };

                    case _pp(PAGES.FAMILY_AND_HOUSEHOLD_SUMMARY_PAGE):
                        return () => {
                            const [
                                hhmIndex
                            ] = args;

                            setException(
                                EXCEPTIONS.REVIEW_HOUSEHOLD_SECTION,
                                [ _pp(PAGES.PERSONAL_INFORMATION_PAGE, hhmIndex) ],
                                _pp(PAGES.PERSONAL_INFORMATION_PAGE, hhmIndex)
                            );
                        };

                    case _pp(PAGES.EXTRA_FINAL_APPLICATION_REVIEW_PAGE):
                        return () => {
                            const [
                                hhmIndex,
                                destinationPage
                            ] = args;

                            changePage(_pp(destinationPage, hhmIndex));
                        };

                    case _pp(PAGES.FINAL_APPLICATION_REVIEW_PAGE):
                        return () => {
                            const [
                                hhmIndex,
                                destinationPage
                            ] = args;

                            changePage(_pp(destinationPage, hhmIndex));
                        };

                    case _pp(PAGES.SIGN_AND_SUBMIT_PAGE):
                        return () => methods.Page3_17.handleEditClick.apply(this, args);

                    case _pp(PAGES.PERSONAL_INCOME_SUMMARY_PAGE, currentHhmIndex):
                        return () => {
                            const [
                                hhmIndex
                            ] = args;

                            setException(
                                EXCEPTIONS.EDITING_INCOMES_AND_DEDUCTIONS,
                                [ _pp(PAGES.INCOME_SOURCES_PAGE, hhmIndex) ],
                                _pp(PAGES.INCOME_SOURCES_PAGE, hhmIndex)
                            );
                        };

                    case _pp(PAGES.INCOME_INFORMATION_SUMMARY_PAGE):
                        return () => {
                            const [
                                hhmIndex,
                                destinationPage
                            ] = args;

                            setException(
                                EXCEPTIONS.REVIEW_INCOME_SECTION,
                                [ _pp(destinationPage, hhmIndex) ],
                                _pp(destinationPage, hhmIndex)
                            );
                        };

                    case _pp(PAGES.ADDITIONAL_INFORMATION_SUMMARY_PAGE):
                        return () => {
                            const [
                                hhmIndex
                            ] = args;

                            setException(
                                EXCEPTIONS.EDITING_ADDITIONAL_INFORMATION,
                                [ _pp(PAGES.OTHER_HEALTH_COVERAGE_PAGE, hhmIndex) ],
                                _pp(PAGES.OTHER_HEALTH_COVERAGE_PAGE, hhmIndex)
                            );
                        };

                    case _pp(PAGES.INCOME_SOURCES_PAGE, currentHhmIndex):
                        return () => methods.Page3_32_E1.handleIncomeSourceEditing.apply(this, args);

                    case _pp(PAGES.DEDUCTION_SOURCES_PAGE, currentHhmIndex):
                        return () => methods.Page3_34_E1.handleDeductionSourceEditing.apply(this, args);
                }
                break;

            case 'removeMemberButton':
                switch(page) {
                    case _pp(PAGES.ABOUT_YOUR_HOUSEHOLD_PAGE):
                        return () => methods.Page1_05.openRemoveMemberModal.apply(this, args);
                }
                break;

            case 'addEmployerCoverageDetailButton':
                return () => methods.Page4_63.handleAddEmployerDetailsClick.apply(this, args);

            case 'addHraDetailButton':
                return () => methods.Page4_67_E5.handleAddEmployerDetailsClick.apply(this, args);

            case 'addIncomeSourceButton':
                return () => methods.Page3_32_E1.handleAddIncomeSourceClick.apply(this, args);

            case 'addDeductionSourceButton':
                return () => methods.Page3_34_E1.handleAddDeductionSourceClick.apply(this, args);

            case 'removeButton_with_icon':
                switch(page) {
                    case _pp(PAGES.INCOME_SOURCES_PAGE, currentHhmIndex):
                        return () => methods.Page3_32_E1.handleIncomeSourceDeletion.apply(this, args);
                    case _pp(PAGES.DEDUCTION_SOURCES_PAGE, currentHhmIndex):
                        return () => methods.Page3_34_E1.handleDeductionSourceDeletion.apply(this, args);
                } break;

            case 'addPersonButton':
                switch(page) {
                    case _pp(PAGES.ABOUT_YOUR_HOUSEHOLD_PAGE):
                        return () => methods.Page1_05.handleAddingNewPerson.apply(this, args);

                    case _pp(PAGES.TAX_HOUSEHOLD_COMPOSITION_PAGE, currentHhmIndex):
                        return () => {
                            setException(
                                EXCEPTIONS.ADDING_HOUSEHOLD_MEMBER_FROM_HOUSEHOLD_SECTION,
                                [ _pp(PAGES.ABOUT_YOUR_HOUSEHOLD_PAGE) ],
                                true
                            );
                        };
                } break;

            case 'fixHouseholdMemberButton':
                return () => {
                    setException(
                        EXCEPTIONS.ADDING_HOUSEHOLD_MEMBER_FROM_HOUSEHOLD_SECTION,
                        [ _pp(PAGES.ABOUT_YOUR_HOUSEHOLD_PAGE) ],
                        true
                    );
                };

            case 'fixRelationshipButton':
                return () => {
                    setException(
                        EXCEPTIONS.MANAGE_SPOUSE,
                        [ _pp(PAGES.RELATIONSHIPS_PAGE), _pp(PAGES.MARITAL_STATUS, currentHhmIndex) ],
                        true
                    );
                };

            case 'updateInformationButton':
                switch(page) {
                    case _pp(PAGES.TAX_HOUSEHOLD_COMPOSITION_PAGE):
                        return () => {
                            setException(
                                EXCEPTIONS.FIXING_TAX_RELATIONSHIP,
                                [ _pp(PAGES.ABOUT_YOUR_HOUSEHOLD_PAGE) ],
                                true
                            );
                        };
                    case _pp(PAGES.MARITAL_STATUS, currentHhmIndex):
                        return () => {
                            setException(
                                EXCEPTIONS.MANAGE_SPOUSE,
                                [ _pp(PAGES.ABOUT_YOUR_HOUSEHOLD_PAGE), _pp(PAGES.MARITAL_STATUS, currentHhmIndex) ],
                                true
                            );
                        };
                } break;

            case 'printButton':
                return () => {
                    methods.Page3_16.handleSummaryOutput.apply(this, args);
                    window.dataLayer.push({ event: 'print_btn_click' });
                };

            case 'downloadButton':
                return () => {
                    methods.Page3_16.handleSummaryOutput.apply(this, args);
                    window.dataLayer.push({ event: 'download_btn_click' });
                };

            default: return () => console.log('Buttons actions haven\'t got because of wrong TYPE!');
        }
    };

    const buttonParams = {
        exception,
        page,
        type,
        adjustedFlow,
        isJumpingRestricted,
        appSummaryPageIndex: _pp(PAGES.EXTRA_FINAL_APPLICATION_REVIEW_PAGE)
    };

    const {
        className,
        isShown,
        i18n,
        ...restButtonProps
    } = _destructureProxy(getButtons(buttonParams));
    
    if(!isShown) {
        return null;
    }

    let uniqueIdValue = uniqueId('id');
    const buttonId = `Page_${page}_${type}_${uniqueIdValue}`;
    const buttonClasses = classnames( className, customButtonClassName);
    const outerClasses = classnames('d-inline-block');
    const RenderedButton = (
        <Button { ...{
            id: buttonId,
            name: type,
            type: 'button',
            classes: buttonClasses,
            outerClass: outerClasses,
            btnText: i18n,
            handleClick: getActions(),
            dataTestId: buttonId,
            disabled: isDisabled,
            ariaLabel: ariaLabel,
            ...restButtonProps
            
        }}/>
    );

    return (
        <Fragment>
            {
                withTooltip
                    ? (
                        <Tooltip
                            content = { tooltipContent }
                            contentReplacement = { tooltipContentReplacement }
                            id = { RenderedButton }
                            triggerDecorationClassName={'border-none'}
                        />
                    )
                    : RenderedButton
            }
        </Fragment>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,

    adjustedFlow: state.ssap.adjustedFlow,
    data: state.ssap.data,
    exception: state.ssap.exception,
    flow: state.ssap.flow,
    form: state.ssap.form,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember,

    hyperReview: state.ssap.hyperReview,
    isSsapFormsInInitialsState: isFormInInitialState(state),
    methods: state.ssap.methods,
    page: state.ssap.page
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData,
        changePage,
        checkBloodRelationships,
        hideAllModals,
        hideModal,
        scrollToElement,
        setException,
        showModal,
        submitForm,
        updateDisabledUi
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(ButtonContainer);
